import { useEffect, useMemo, useState } from "react";
import api from "@/api";
import { useLang } from "@/i18n";
import Layout from "@/components/Layout";
import { ClientLayout } from "./ClientHome";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { TrendingUp } from "lucide-react";

export default function History() {
  const { t } = useLang();
  const [logs, setLogs] = useState(null);
  const [chartEx, setChartEx] = useState("");

  useEffect(() => {
    api.get("/client/logs").then((r) => setLogs(r.data));
  }, []);

  const exerciseOptions = useMemo(() => {
    const map = new Map();
    for (const log of logs || []) {
      for (const e of log.entries || []) {
        if ((e.sets || []).some((s) => s.done && parseFloat(s.weight) > 0)) {
          map.set(e.exercise_id, e.exercise_name);
        }
      }
    }
    return [...map.entries()];
  }, [logs]);

  useEffect(() => {
    if (!chartEx && exerciseOptions.length) setChartEx(exerciseOptions[0][0]);
  }, [exerciseOptions]);

  const chartData = useMemo(() => {
    if (!chartEx || !logs) return [];
    return [...logs].reverse().map((log) => {
      const e = (log.entries || []).find((x) => x.exercise_id === chartEx);
      if (!e) return null;
      const done = (e.sets || []).filter((s) => s.done && parseFloat(s.weight) > 0);
      if (!done.length) return null;
      return {
        date: (log.date || "").slice(5, 10),
        weight: Math.max(...done.map((s) => parseFloat(s.weight) || 0)),
        volume: done.reduce((a, s) => a + (parseFloat(s.weight) || 0) * (parseInt(s.reps) || 0), 0),
      };
    }).filter(Boolean);
  }, [logs, chartEx]);

  return (
    <ClientLayout>
      <div data-testid="history-page" className="max-w-2xl mx-auto w-full">
        <h1 className="text-3xl font-black uppercase tracking-tight mb-6">{t("hist.title")}</h1>
        {!logs ? (
          <p className="text-muted-foreground">{t("common.loading")}</p>
        ) : logs.length === 0 ? (
          <p className="text-muted-foreground" data-testid="history-empty">{t("hist.empty")}</p>
        ) : (
          <>
            {exerciseOptions.length > 0 && (
              <div className="kc-card p-4 mb-6" data-testid="progress-card">
                <p className="kc-eyebrow flex items-center gap-2 mb-3"><TrendingUp className="w-4 h-4" />{t("hist.progress")}</p>
                <select data-testid="progress-exercise-select" value={chartEx} onChange={(e) => setChartEx(e.target.value)} className="kc-input mb-4">
                  {exerciseOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                </select>
                {chartData.length < 2 ? (
                  <p className="text-sm text-muted-foreground" data-testid="progress-empty">{t("hist.noProgress")}</p>
                ) : (
                  <div data-testid="progress-chart" className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData} margin={{ top: 5, right: 8, left: -18, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="date" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} stroke="hsl(var(--border))" />
                        <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} stroke="hsl(var(--border))" unit="kg" width={48} />
                        <Tooltip
                          contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12 }}
                          formatter={(v, name) => name === "volume"
                            ? [`${Math.round(v)} kg·rep`, t("hist.volume")]
                            : [`${v} kg`, t("hist.weight")]} />
                        <Line type="monotone" dataKey="weight" stroke="hsl(var(--primary))" strokeWidth={2.5}
                          dot={{ r: 3, fill: "hsl(var(--primary))" }} name="weight" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}
            <div className="space-y-3">
              {logs.map((log) => (
                <details key={log._id} className="kc-card p-4 group" data-testid="history-log-card">
                  <summary className="flex justify-between items-center cursor-pointer list-none">
                    <span className="font-bold">{log.routine_name || "—"}</span>
                    <span className="text-sm text-muted-foreground font-mono">{log.date?.slice(0, 10)}</span>
                  </summary>
                  <div className="mt-3 pt-3 border-t border-border text-sm space-y-2">
                    {(log.entries || []).map((e, i) => (
                      <div key={i}>
                        <p className="font-semibold">{e.exercise_name}</p>
                        <p className="text-muted-foreground font-mono">
                          {(e.sets || []).filter((s) => s.done).map((s) => `${s.weight}kg×${s.reps}`).join("  ·  ") || "—"}
                        </p>
                        {e.notes && <p className="text-muted-foreground italic text-xs">"{e.notes}"</p>}
                      </div>
                    ))}
                    {log.notes && <p className="text-muted-foreground italic">"{log.notes}"</p>}
                  </div>
                </details>
              ))}
            </div>
          </>
        )}
      </div>
    </ClientLayout>
  );
}
